import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { toast } from 'sonner';

import { useMatter } from '@/hooks/useMatter';
import {
  countBlockingMissingFields,
  hasBlockingMissingFields,
} from '@/lib/missingFields';
import { useMattersHipaaAuthorizationStatusRetrieveQuery } from '@/services/api/generated/hipaaESignService';
import type { TreatmentRecordRequestStatus } from '@/services/api/generated/treatmentRecordRequestsService';
import { useHipaaAuthorizationRetrieveQuery } from '@/services/api/hipaaAuthorizationService';
import {
  useGetPackageQuery,
  usePublishPackageMutation,
  useRebuildPackagePdfMutation,
  useRenderPackageMutation,
} from '@/services/api/hipaaPackageService';
import { useGetMatterFlattenedContextQuery } from '@/services/api/matterFlattenedContextService';
import { useGetMatterQuery } from '@/services/api/mattersService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';
import { useGetMedicalProviderQuery } from '@/services/api/treatmentService';

import { hasManualPackageContent } from '../review-submit/utils';
import { hasSatisfiedHipaaSignatureRequirement } from '../signatureRequirement';
import type {
  HipaaPackageSchema,
  MatterFlattenedContext,
  MedicalProvider,
  ModalState,
  PackageMode,
  PackageRequestInfo,
  PackageValidationStatus,
  TreatmentRecordRequestSchema,
  ViewMode,
} from '../types';

// ============================================================================
// Modal Actions
// ============================================================================

type ModalAction =
  | { type: 'OPEN_MODAL'; modal: keyof ModalState }
  | { type: 'CLOSE_MODAL'; modal: keyof ModalState }
  | { type: 'CLOSE_ALL_MODALS' };

// ============================================================================
// Context Value Interface
// ============================================================================

interface RequestPageContextValue {
  // Core IDs
  matterId: string;
  providerId: string;
  requestId: string;

  // Request info for API calls
  requestInfo: PackageRequestInfo;

  // Primary Data
  request: TreatmentRecordRequestSchema | undefined;
  provider: MedicalProvider | undefined;
  matter: ReturnType<typeof useGetMatterQuery>['data'];
  packageData: HipaaPackageSchema | undefined;
  matterContext: MatterFlattenedContext | undefined;

  // Loading states
  isLoadingRequest: boolean;
  isLoadingProvider: boolean;
  isLoadingPackage: boolean;
  isLoadingMatterContext: boolean;

  // Computed values
  primaryClient:
    | ReturnType<typeof useGetMatterQuery>['data']['clients'][0]
    | undefined;
  clientName: string | undefined;
  hasMissingSignature: boolean;
  validationStatus: PackageValidationStatus;
  /** Whether the package can currently be edited */
  canEditPackage: boolean;
  /** Whether the request has been submitted at least once */
  hasSubmittedRequest: boolean;

  // Package UI state
  packageMode: PackageMode;
  setPackageMode: (mode: PackageMode) => void;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  closeRequestPage: () => void;

  // Modal management
  modals: ModalState;
  openModal: (modal: keyof ModalState) => void;
  closeModal: (modal: keyof ModalState) => void;
  closeAllModals: () => void;

  // Actions
  refetchRequest: ReturnType<
    typeof treatmentRecordRequestService.useMattersProvidersRecordRequestsRetrieveQuery
  >['refetch'];
  refetchPackage: ReturnType<typeof useGetPackageQuery>['refetch'];
  handleRender: () => Promise<void>;
  handlePublish: () => Promise<void>;
  isRendering: boolean;
  isPublishing: boolean;
}

const SUBMITTED_STATUSES: TreatmentRecordRequestStatus[] = [
  'request_submitted',
  'request_received',
  'awaiting_invoice',
  'pending_payment',
  'records_pending',
  'records_delivered',
  'records_ingested',
  'rejected',
  'cancelled',
];

// ============================================================================
// Modal Reducer
// ============================================================================

const modalReducer = (state: ModalState, action: ModalAction): ModalState => {
  switch (action.type) {
    case 'OPEN_MODAL':
      return { ...state, [action.modal]: true };
    case 'CLOSE_MODAL':
      return { ...state, [action.modal]: false };
    case 'CLOSE_ALL_MODALS':
      return {
        upload: false,
        clientInfo: false,
        signature: false,
        requestMethod: false,
        eSign: false,
      };
    default:
      return state;
  }
};

// ============================================================================
// Context & Provider
// ============================================================================

const RequestPageContext = createContext<RequestPageContextValue | null>(null);

interface RequestPageProviderProps {
  requestId: string;
  providerId: string;
  onClose?: () => void;
  children: ReactNode;
}

export const RequestPageProvider = ({
  requestId,
  providerId,
  onClose,
  children,
}: RequestPageProviderProps) => {
  const { matterId } = useMatter();

  const requestInfo: PackageRequestInfo = useMemo(
    () => ({ matterId, providerId, requestId }),
    [matterId, providerId, requestId],
  );

  // ======== Data Fetching ========

  // Fetch request data
  const {
    data: request,
    isLoading: isLoadingRequest,
    refetch: refetchRequest,
  } = treatmentRecordRequestService.useMattersProvidersRecordRequestsRetrieveQuery(
    {
      matterId,
      providerId,
      requestId,
    },
  );

  // Fetch provider data
  const { data: provider, isLoading: isLoadingProvider } =
    useGetMedicalProviderQuery({
      matterId,
      providerId,
    });

  // Fetch matter data for client info
  const { data: matter } = useGetMatterQuery(matterId);

  // Fetch matter context for component rendering
  const { data: matterContext, isLoading: isLoadingMatterContext } =
    useGetMatterFlattenedContextQuery(
      {
        matterId,
        treatmentRecordRequestId: request?.id,
        treatmentChannelId: request?.channel?.id,
        medicalNoteId: providerId,
      },
      { skip: !request?.id },
    );

  // ======== Computed Values ========

  const primaryClient = useMemo(() => matter?.clients?.[0], [matter?.clients]);

  const clientName = useMemo(() => {
    if (!primaryClient) return undefined;
    const fullName = primaryClient.name?.trim();
    if (fullName) return fullName;
    const first = primaryClient.first_name || '';
    const last = primaryClient.last_name || '';
    return `${first} ${last}`.trim() || undefined;
  }, [primaryClient]);

  // Check for HIPAA authorization signature
  const { data: hipaaAuthorization } = useHipaaAuthorizationRetrieveQuery(
    { clientId: primaryClient?.id || '' },
    { skip: !primaryClient?.id },
  );
  const { data: hipaaESignStatus } =
    useMattersHipaaAuthorizationStatusRetrieveQuery(
      { matterId },
      { skip: !matterId },
    );

  // ======== Layout State ========

  const [viewMode, setViewMode] = useState<ViewMode>('review');

  // ======== Package Management ========

  const [packageModeState, setPackageModeState] = useState<PackageMode>('view');
  const [hasInitializedMode, setHasInitializedMode] = useState(false);
  const [shouldPoll, setShouldPoll] = useState(false);

  // Fetch package data with conditional polling
  const {
    data: packageData,
    isLoading: isLoadingPackage,
    refetch: refetchPackage,
  } = useGetPackageQuery(requestInfo, {
    skip: !request?.id,
    pollingInterval: shouldPoll ? 3000 : 0,
    skipPollingIfUnfocused: true,
    refetchOnMountOrArgChange: true,
  });

  const hasSignedAuthorizationMerge = useMemo(() => {
    return (
      packageData?.components?.some(
        (component) =>
          component.component_type === 'signed_authorization_merge',
      ) ?? false
    );
  }, [packageData?.components]);

  const hasMissingSignature = !hasSatisfiedHipaaSignatureRequirement({
    hasUploadedSignatureImage: Boolean(
      hipaaAuthorization?.cleaned_signature_url ||
        hipaaAuthorization?.signature_url,
    ),
    hasSignedAuthorizationMerge,
    eSignStatus: hipaaESignStatus,
  });

  // Update polling state when data changes
  useEffect(() => {
    if (!packageData) {
      setShouldPoll(false);
      return;
    }

    const packageGenerating = packageData.status === 'generating';
    const componentsGenerating =
      packageData.components?.some((c) => c.status === 'generating') || false;
    const needsPolling = packageGenerating || componentsGenerating;

    setShouldPoll(needsPolling);
  }, [packageData]);

  const hasSubmittedRequest = useMemo(() => {
    if (!request?.status) return false;
    return SUBMITTED_STATUSES.includes(request.status);
  }, [request?.status]);

  const canEditPackage = !hasSubmittedRequest;

  useEffect(() => {
    if (!hasInitializedMode && packageData) {
      const hasManualUpload =
        packageData.package_source === 'manually_uploaded' &&
        !!packageData.download_url;

      // Check actual missing fields instead of trusting the status string
      const hasActualMissingFields = packageData.components?.some(
        (c) =>
          c.component_type !== 'signed_authorization_merge' &&
          hasBlockingMissingFields(c.missing_fields),
      );

      const shouldEditMode =
        canEditPackage &&
        !hasManualUpload &&
        (!packageData.rendered_pdf_url ||
          hasActualMissingFields ||
          packageData.status === 'error' ||
          packageData.status === 'ready' ||
          packageData.status === 'missing_fields');

      const nextMode: PackageMode =
        shouldEditMode && canEditPackage ? 'edit' : 'view';
      setPackageModeState(nextMode);
      setHasInitializedMode(true);
    }
  }, [packageData, hasInitializedMode, canEditPackage]);

  useEffect(() => {
    if (!canEditPackage && packageModeState === 'edit') {
      setPackageModeState('view');
    }
  }, [canEditPackage, packageModeState]);

  const setPackageMode = useCallback(
    (mode: PackageMode) => {
      if (mode === 'edit' && !canEditPackage) {
        return;
      }
      setPackageModeState(mode);
    },
    [canEditPackage],
  );

  // Compute validation status
  // Note: signed_authorization_merge components are excluded from blocking validation
  // since their missing fields are optional and shouldn't prevent submission
  const validationStatus: PackageValidationStatus = useMemo(() => {
    if (!packageData?.components) {
      return {
        componentCount: 0,
        hasValidationIssues: false,
        hasMissingFields: false,
        missingFieldsCount: 0,
      };
    }

    const { components } = packageData;

    // Filter out signed_authorization_merge components for validation blocking
    const blockingComponents = components.filter(
      (c) => c.component_type !== 'signed_authorization_merge',
    );

    // Only consider actual missing fields, not just the status string.
    // The backend may return status='missing_fields' even when the dict is empty.
    const hasMissingFields = blockingComponents.some((c) =>
      hasBlockingMissingFields(c.missing_fields),
    );

    const missingFieldsCount = blockingComponents.reduce((total, component) => {
      return total + countBlockingMissingFields(component.missing_fields);
    }, 0);

    return {
      componentCount: components.length,
      hasValidationIssues: hasMissingFields,
      hasMissingFields,
      missingFieldsCount,
    };
  }, [packageData]);

  // ======== Modal Management ========

  const [modals, dispatch] = useReducer(modalReducer, {
    upload: false,
    clientInfo: false,
    signature: false,
    requestMethod: false,
    eSign: false,
  });

  const openModal = useCallback((modal: keyof ModalState) => {
    dispatch({ type: 'OPEN_MODAL', modal });
  }, []);

  const closeModal = useCallback((modal: keyof ModalState) => {
    dispatch({ type: 'CLOSE_MODAL', modal });
  }, []);

  const closeAllModals = useCallback(() => {
    dispatch({ type: 'CLOSE_ALL_MODALS' });
  }, []);

  // ======== Package Actions ========

  const [renderPackage, { isLoading: isRendering }] =
    useRenderPackageMutation();
  const [rebuildPackagePdf, { isLoading: isRebuildingPdf }] =
    useRebuildPackagePdfMutation();
  const [publishPackage, { isLoading: isPublishing }] =
    usePublishPackageMutation();
  const [autoRenderRequestId, setAutoRenderRequestId] = useState<string | null>(
    null,
  );

  const handleRender = useCallback(async () => {
    try {
      if (hasManualPackageContent(packageData?.components)) {
        await rebuildPackagePdf(requestInfo).unwrap();
        toast.success('Package PDF refreshed');
        return;
      }

      await renderPackage(requestInfo).unwrap();
      toast.success('Package rendering started');
    } catch {
      toast.error('Failed to start rendering');
    }
  }, [packageData?.components, rebuildPackagePdf, renderPackage, requestInfo]);

  // Auto-render package when it has components but hasn't been rendered yet
  useEffect(() => {
    // Skip if already auto-rendered for this request or currently rendering
    if (
      autoRenderRequestId === requestId ||
      isRendering ||
      isRebuildingPdf ||
      !packageData
    )
      return;

    const hasComponents = (packageData.components?.length ?? 0) > 0;
    const isManualUpload = packageData.package_source === 'manually_uploaded';
    const hasRenderedPdf = !!packageData.rendered_pdf_url;
    const isGenerating = packageData.status === 'generating';
    const isPublished = packageData.status === 'published';
    const hasManualContent = hasManualPackageContent(packageData.components);

    // Auto-render if: has components, not manual upload, no rendered PDF,
    // and not already generating or published
    if (
      hasComponents &&
      !isManualUpload &&
      !hasRenderedPdf &&
      !isGenerating &&
      !isPublished
    ) {
      const renderPromise = hasManualContent
        ? rebuildPackagePdf(requestInfo).unwrap()
        : renderPackage(requestInfo).unwrap();

      renderPromise
        .then(() => {
          // Only mark as auto-rendered on success
          setAutoRenderRequestId(requestId);
        })
        .catch(() => {
          // Don't mark as auto-rendered on failure, allowing retry
        });
    }
  }, [
    packageData,
    autoRenderRequestId,
    requestId,
    isRendering,
    isRebuildingPdf,
    rebuildPackagePdf,
    renderPackage,
    requestInfo,
  ]);

  const handlePublish = useCallback(async () => {
    try {
      await publishPackage(requestInfo).unwrap();
      toast.success('Package approved successfully');
      await refetchPackage();
    } catch {
      toast.error('Failed to approve package');
    }
  }, [publishPackage, requestInfo, refetchPackage]);

  // ======== Context Value ========

  const value: RequestPageContextValue = useMemo(
    () => ({
      // Core IDs
      matterId,
      providerId,
      requestId,
      requestInfo,

      // Data
      request,
      provider,
      matter,
      packageData,
      matterContext,

      // Loading states
      isLoadingRequest,
      isLoadingProvider,
      isLoadingPackage,
      isLoadingMatterContext,

      // Computed values
      primaryClient,
      clientName,
      hasMissingSignature,
      validationStatus,

      // Package UI state
      canEditPackage,
      hasSubmittedRequest,
      packageMode: packageModeState,
      setPackageMode,
      viewMode,
      setViewMode,
      closeRequestPage: () => onClose?.(),

      // Modal management
      modals,
      openModal,
      closeModal,
      closeAllModals,

      // Actions
      refetchRequest,
      refetchPackage,
      handleRender,
      handlePublish,
      isRendering,
      isPublishing,
    }),
    [
      matterId,
      providerId,
      requestId,
      requestInfo,
      request,
      provider,
      matter,
      packageData,
      matterContext,
      isLoadingRequest,
      isLoadingProvider,
      isLoadingPackage,
      isLoadingMatterContext,
      primaryClient,
      clientName,
      hasMissingSignature,
      validationStatus,
      packageModeState,
      setPackageMode,
      canEditPackage,
      hasSubmittedRequest,
      viewMode,
      onClose,
      modals,
      openModal,
      closeModal,
      closeAllModals,
      refetchRequest,
      refetchPackage,
      handleRender,
      handlePublish,
      isRendering,
      isPublishing,
      setViewMode,
    ],
  );

  return (
    <RequestPageContext.Provider value={value}>
      {children}
    </RequestPageContext.Provider>
  );
};

// ============================================================================
// Hook
// ============================================================================

export const useRequestPage = () => {
  const context = useContext(RequestPageContext);
  if (!context) {
    throw new Error('useRequestPage must be used within RequestPageProvider');
  }
  return context;
};
